<template>
  <div
    class="flex flex-column h-100 w-100 pos-relative o-hidden"
    id="app-wrapper"
    :class="{
      'bg-white': headerTheme === 'light',
      'bg-black': headerTheme === 'dark',
    }"
  >
    <Preloader v-if="showPreloader" @complete="onPreloaderComplete" />

    <component
      v-if="route.meta.header"
      :is="route.meta.header"
      :theme="headerTheme"
      :logotype="route.meta.logotype"
      :location="route.meta.location"
      :showCart="route.meta.header_show_cart"
      :showProfile="route.meta.header_show_profile"
      :showNotifications="route.meta.header_show_notifications"
    />

    <section
      class="flex flex-nowrap h-100 pos-relative o-hidden"
      id="screen"
      ref="screen"
    >
      <component
        v-if="route.meta?.sidebar"
        :is="route.meta.sidebar"
        :stateSidebar="store.core.state.isOpenSidebar"
        :widthHidden="route.meta?.sidebar_width_hidden"
        :width="route.meta?.sidebar_width"
        :theme="headerTheme"
        @closeSidebar="() => (store.core.state.isOpenSidebar = false)"
      >
        <template #header>
          <component v-if="route.meta?.sidebar_header_component" :is="route.meta.sidebar_header_component" :theme="headerTheme" />
        </template>

        <transition name="moveFromTop" mode="out-in">
          <component
            v-if="route.meta?.sidebar_navigation"
            :is="route.meta.sidebar_navigation"
            :key="route.meta.sidebar_navigation_items"
            :navigationItems="route.meta.sidebar_navigation_items"
            v-model:stateSidebar="store.core.state.isOpenSidebar"
            :theme="headerTheme"
          />
        </transition>

        <template #footer>
          <component v-if="route.meta?.sidebar_footer_component" :is="route.meta.sidebar_footer_component" :theme="headerTheme" :stateSidebar="store.core.state.isOpenSidebar" />
        </template>
      </component>

      <div class="rows-[minmax(0,1fr)] z-index-1 pos-relative w-100 h-100">
        <div class="o-y-scroll o-x-hidden h-100" id="scrollview" ref="scrollview">
          <div class="h-min-100 pos-relative w-100">
            <router-view
              class="h-min-100 pos-relative w-100"
              id="view"
              v-slot="{ Component, route: viewRoute }"
            >
              <transition name="scaleTransition" mode="out-in" @before-enter="scrollTop">
                <div class="w-100 h-min-100" :key="viewRoute.path">
                  <component class="w-100 h-min-100" :is="Component" />
                  <component
                    v-if="viewRoute.meta.footer"
                    :is="viewRoute.meta.footer"
                    :dark="viewRoute.meta.darkTheme"
                    :theme="viewRoute.meta.footer_theme || 'light'"
                    :logotype="viewRoute.meta.logotype"
                  />
                </div>
              </transition>
            </router-view>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useHead } from '@unhead/vue'
import { useStore } from '@ozdao/martyrs/modules/core/client'

import Preloader from '@/components/partials/PreloaderCounter.vue'

const store = useStore()
const route = useRoute()
const scrollview = ref(null)
const showPreloader = ref(true)

const headerTheme = computed(() => route.meta?.header_theme || 'light')

const onPreloaderComplete = () => {
  showPreloader.value = false
}

function scrollTop() {
  if (scrollview.value) scrollview.value.scrollTop = 0
}

useHead({
  script: [
    {
      innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PWFHT42');`,
    },
  ],
})
</script>

<style lang="scss">
.moveFromTop-enter-active,
.moveFromTop-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.moveFromTop-enter-from,
.moveFromTop-leave-to {
  transform: translateY(-1rem);
  opacity: 0;
}

.scaleTransition-enter-active,
.scaleTransition-leave-active {
  transform: translateY(0px);
  opacity: 1;
  z-index: 1;
  transition: all 0.4s ease;
}

.scaleTransition-enter-from,
.scaleTransition-leave-to {
  opacity: 0;
  z-index: 0;
  position: absolute;
  width: inherit;
  height: inherit;
  transform: translateY(30px);
  transition: all 0.4s ease;
}
</style>
